export const accountingArticles = [
    {
        id: 'accounting.overview',
        title: 'Accounting Overview',
        category: 'Accounting',
        order: 1,
        summary: 'Financial management and transaction processing',
        content: `
The Accounting page provides comprehensive financial management including invoicing, payments, progress claims, and accounting integrations.

**Tabs:**

- **Dashboard** - Key financial metrics and overview
- **Invoices** - View and manage supplier invoices
- **Invoice Entry** - Enter new invoices
- **PO Matching** - Match invoices to purchase orders
- **Payment Batches** - Process supplier payments
- **Audit Trail** - View all financial transaction history
- **Progress Claim** - Create client claims
- **Budget Progress** - Track budget vs actual
- **Claims History** - View past claims
- **Receipts** - Record client payments
- **Settings** - Accounting configuration, integrations, and ABA setup
    `,
        expandableSections: [
            {
                title: 'Financial Workflow',
                content: `
**Payables Flow:**
1. Receive supplier invoice
2. Enter invoice in system
3. Match to purchase order
4. Create payment batch
5. Generate ABA file
6. Process payment

**Receivables Flow:**
1. Complete work stages
2. Create progress claim
3. Send to client
4. Record receipt when paid
        `,
            },
            {
                title: 'Accounting Integrations',
                content: `
The accounting module supports integration with popular accounting platforms:

- **Xero** — Sync invoices, payments, claims, and receipts
- **QuickBooks** — Full two-way sync with QuickBooks Online
- **MYOB** — Connect to MYOB for financial data exchange

Configure integrations in the **Settings** tab within Accounting. See the Integrations help article for details.
        `,
            },
        ],
        keywords: ['accounting', 'finance', 'invoices', 'payments', 'claims', 'xero', 'myob', 'quickbooks', 'integrations'],
        route: '/accounting',
        relatedArticles: ['accounting.invoices', 'accounting.payments', 'accounting.claims', 'accounting.integrations'],
        screenshots: [
            {
                src: 'accounting/accounting-invoices.png',
                alt: 'Accounting Invoices tab',
                caption: 'The Invoices tab shows all supplier invoices and their status',
            },
        ],
    },
    {
        id: 'accounting.invoices',
        title: 'Viewing Invoices',
        category: 'Accounting',
        order: 2,
        summary: 'Managing and viewing supplier invoices',
        content: `
The Invoices tab shows all supplier invoices with their matching and payment status.

**Invoice Statuses:**

- **Unmatched** - Not matched to an order
- **Matched** - Matched to order, awaiting payment
- **Part Paid** - Partially paid
- **Paid** - Fully paid
    `,
        expandableSections: [
            {
                title: 'Invoice Grid',
                content: `
The grid displays:

- **Invoice Number** - Supplier's reference
- **Supplier** - Who sent the invoice
- **Job** - Associated job
- **Order** - Matched order (if any)
- **Amount** - Invoice total
- **Due Date** - Payment due date
- **Status** - Current status

Filter by status, supplier, job, or date range.
        `,
            },
        ],
        keywords: ['invoices', 'bills', 'supplier invoices', 'payables'],
        route: '/accounting',
        relatedArticles: ['accounting.overview', 'accounting.matching'],
    },
    {
        id: 'accounting.entry',
        title: 'Invoice Entry',
        category: 'Accounting',
        order: 3,
        summary: 'How to enter supplier invoices',
        content: `
Enter supplier invoices to track what you owe and prepare for payment.

**Required Information:**

- Supplier
- Invoice Number
- Invoice Date
- Amount (including GST)
    `,
        expandableSections: [
            {
                title: 'Entering an Invoice',
                content: `
1. Go to **Invoice Entry** tab
2. Select the Supplier
3. Enter invoice details:
   - Invoice Number
   - Invoice Date
   - Due Date
   - Amount
4. Optionally select the Job
5. Upload invoice document (optional)
6. Click **Save**

The invoice appears in the Invoices list as "Unmatched".
        `,
            },
            {
                title: 'Document Attachment',
                content: `
Attach the supplier's invoice document:

1. Click **Attach Document** or drag-drop
2. Select the PDF/image
3. Document is linked to the invoice record

View attached documents from the invoice grid.
        `,
            },
        ],
        keywords: ['invoice entry', 'enter invoice', 'add invoice', 'bill entry'],
        route: '/accounting',
        relatedArticles: ['accounting.overview', 'accounting.matching'],
    },
    {
        id: 'accounting.matching',
        title: 'Invoice Matching',
        category: 'Accounting',
        order: 4,
        summary: 'Matching invoices to purchase orders',
        content: `
Invoice matching verifies that invoices correspond to purchase orders, ensuring you only pay for what was ordered.

**3-Way Matching:**

1. Purchase Order (what was ordered)
2. Delivery/Receipt (what was received)
3. Invoice (what was billed)
    `,
        expandableSections: [
            {
                title: 'Matching Process',
                content: `
1. Go to **Invoice Matching** tab
2. Select an unmatched invoice
3. System shows potential order matches
4. Select the matching order
5. Compare:
   - Ordered quantities vs invoiced
   - Ordered prices vs invoiced
6. Resolve any discrepancies
7. Confirm the match

Matched invoices move to "Matched" status.
        `,
            },
            {
                title: 'Handling Discrepancies',
                content: `
**Quantity Variances:**
- Short delivery - partial match
- Over delivery - requires approval

**Price Variances:**
- Within tolerance - auto-approve
- Outside tolerance - requires review

**No Matching Order:**
- Manual approval required
- Mark as "approved without order"
        `,
            },
        ],
        keywords: ['matching', 'invoice matching', '3-way match', 'verify'],
        route: '/accounting',
        relatedArticles: ['accounting.overview', 'accounting.invoices'],
    },
    {
        id: 'accounting.payments',
        title: 'Payment Batches',
        category: 'Accounting',
        order: 5,
        summary: 'Processing supplier payments and generating ABA files',
        content: `
Create payment batches to pay multiple suppliers at once and generate ABA files for direct deposit.

**Payment Workflow:**

1. Select invoices to pay
2. Create payment batch
3. Review and approve
4. Generate ABA file
5. Upload to bank
6. Mark as paid
    `,
        expandableSections: [
            {
                title: 'Creating a Payment Batch',
                content: `
1. Go to **Payment Batches** tab
2. Click **New Batch**
3. Set batch details:
   - Payment Date
   - Description
4. Select invoices to include:
   - Filter by due date
   - Select specific invoices
5. Review total amount
6. Click **Create Batch**
        `,
            },
            {
                title: 'Generating ABA File',
                content: `
1. Open the payment batch
2. Verify supplier banking details
3. Click **Generate ABA**
4. Download the ABA file
5. Upload to your bank portal

**ABA Settings:**
Configure your bank details in Settings > Accounting > ABA Configuration.
        `,
            },
            {
                title: 'Completing Payment',
                content: `
After uploading to bank:

1. Return to the batch
2. Click **Mark as Paid**
3. Enter confirmation details
4. Invoices update to "Paid" status

This records the payment in the system.
        `,
            },
        ],
        keywords: ['payments', 'pay', 'ABA', 'direct deposit', 'bank', 'batch'],
        route: '/accounting',
        relatedArticles: ['accounting.overview', 'accounting.invoices', 'settings.aba'],
        screenshots: [
            {
                src: 'accounting/accounting-payment-batches.png',
                alt: 'Payment Batches',
                caption: 'Create payment batches and generate ABA files for direct deposit',
            },
        ],
    },
    {
        id: 'accounting.claims',
        title: 'Progress Claims',
        category: 'Accounting',
        order: 6,
        summary: 'Creating and managing progress claims',
        content: `
Progress claims allow you to invoice clients for work completed on a job.

**Claim Types:**

- **Stage-Based** - Based on construction stages
- **Percentage-Based** - Based on overall completion
- **Milestone-Based** - Based on achieved milestones
    `,
        expandableSections: [
            {
                title: 'Creating a Claim',
                content: `
1. Go to **Progress Claims** tab
2. Select the Job
3. Click **New Claim**
4. Choose claim method:
   - Select stages completed
   - Or enter completion percentage
5. Add variations (if any)
6. Review claim total
7. Generate claim document
8. Send to client
        `,
            },
            {
                title: 'Claim Document',
                content: `
The claim document includes:

- Job and client details
- Claimed stages/percentages
- Previous claims summary
- Current claim amount
- Retention (if applicable)
- GST and total
- Payment instructions

Download as PDF or email directly.
        `,
            },
        ],
        keywords: ['claims', 'progress claims', 'billing', 'invoice client'],
        route: '/accounting',
        relatedArticles: ['accounting.overview', 'accounting.receipts', 'jobs.claims'],
        screenshots: [
            {
                src: 'accounting/accounting-progress-claims.png',
                alt: 'Progress Claims',
                caption: 'Create and manage progress claims for client billing',
            },
        ],
    },
    {
        id: 'accounting.receipts',
        title: 'Recording Receipts',
        category: 'Accounting',
        order: 7,
        summary: 'Recording client payments against claims',
        content: `
Record client payments when they pay progress claims or other invoices.

**Receipt Information:**

- Receipt date
- Amount received
- Payment method
- Reference number
    `,
        expandableSections: [
            {
                title: 'Recording a Receipt',
                content: `
1. Go to **Receipts** tab
2. Click **New Receipt**
3. Select the Job
4. Select the claim/invoice being paid
5. Enter:
   - Receipt Date
   - Amount Received
   - Payment Method
   - Bank Reference
6. Click **Save**

The claim status updates to "Paid" or "Part Paid".
        `,
            },
            {
                title: 'Partial Payments',
                content: `
If client pays less than the full amount:

1. Enter the actual amount received
2. Claim shows as "Part Paid"
3. Remaining balance tracked
4. Additional receipts can be recorded
        `,
            },
        ],
        keywords: ['receipts', 'payments received', 'client payments', 'receivables'],
        route: '/accounting',
        relatedArticles: ['accounting.overview', 'accounting.claims'],
    },
    {
        id: 'accounting.budget',
        title: 'Budget Progress',
        category: 'Accounting',
        order: 8,
        summary: 'Tracking budget vs actual expenditure',
        content: `
The Budget Progress tab shows how actual costs compare to budgeted amounts by cost centre.

**Key Metrics:**

- Budget (from BOQ)
- Committed (orders placed)
- Actual (invoiced/paid)
- Variance
    `,
        expandableSections: [
            {
                title: 'Reading the Report',
                content: `
Each cost centre shows:

| Column | Description |
|--------|-------------|
| Budget | BOQ total for the cost centre |
| Committed | Value of placed orders |
| Actual | Invoiced amounts |
| Variance | Budget - Actual |

**Status Indicators:**
- Green: Under budget
- Yellow: Within tolerance
- Red: Over budget
        `,
            },
        ],
        keywords: ['budget', 'actual', 'variance', 'cost tracking', 'expenditure'],
        route: '/accounting',
        relatedArticles: ['accounting.overview', 'boq.cost-centres'],
    },
    {
        id: 'accounting.integrations',
        title: 'Accounting Integrations',
        category: 'Accounting',
        order: 9,
        summary: 'Connect to Xero, QuickBooks, or MYOB for accounting sync',
        content: `
The Integrations panel lets you connect Costed.app to your accounting platform for automatic data synchronisation.

**Supported Platforms:**

- **Xero** — Australian-popular cloud accounting
- **QuickBooks Online** — Intuit's cloud accounting
- **MYOB** — Australian accounting software

Once connected, financial data flows between Costed.app and your accounting platform automatically.
    `,
        expandableSections: [
            {
                title: 'Connecting a Platform',
                content: `
1. Go to **Accounting** > **Settings** tab
2. Click **Integrations**
3. Click **Connect** next to your platform (Xero, QuickBooks, or MYOB)
4. You'll be redirected to the platform's login page
5. Authorise Costed.app to access your account
6. You're returned to Costed.app with the connection active

Only one platform can be connected at a time. To switch, disconnect the current platform first.
        `,
            },
            {
                title: 'GL Mapping',
                content: `
GL (General Ledger) mapping links your local account codes to the corresponding accounts in your connected platform.

1. Go to **Accounting** > **Settings** > **Integrations**
2. Click **GL Mapping**
3. For each local GL account:
   - Select the matching remote account from the dropdown
   - Toggle sync on/off for individual mappings
4. Click **Save**

Mappings ensure that invoices, payments, and journal entries post to the correct accounts in both systems. Unmapped accounts will use your platform's default account.
        `,
            },
            {
                title: 'Auto-Sync Configuration',
                content: `
Configure which data types sync automatically:

| Sync Type | Description |
|-----------|-------------|
| **Invoices** | Supplier invoices |
| **Payments** | Payment batches |
| **Claims** | Progress claims |
| **Receipts** | Client payment receipts |
| **Batch Payments** | ABA batch payment records |
| **Journal Entries** | Manual journal adjustments |
| **Credit Notes** | Credit note transactions |

Enable or disable each sync type independently. Synced records are linked bi-directionally — changes in either system are reflected.
        `,
            },
            {
                title: 'Sync History',
                content: `
View the history of all sync operations:

- **Timestamp** — When the sync occurred
- **Type** — What was synced (invoice, payment, etc.)
- **Direction** — Push (to platform) or Pull (from platform)
- **Status** — Success, Failed, or Skipped
- **Details** — Error messages for failed syncs

Use sync history to troubleshoot issues or verify that data is flowing correctly.
        `,
            },
        ],
        keywords: ['integrations', 'xero', 'quickbooks', 'myob', 'sync', 'gl mapping', 'accounting integration'],
        route: '/accounting',
        relatedArticles: ['accounting.overview', 'settings.accounting'],
    },
    {
        id: 'accounting.audit-trail',
        title: 'Audit Trail',
        category: 'Accounting',
        order: 10,
        summary: 'View history of all financial transactions and changes',
        content: `
The Audit Trail tab provides a complete history of all financial transactions and changes within the accounting module.

**What's Tracked:**

- Invoice creation, matching, and payment
- Payment batch creation and processing
- Progress claim creation and approval
- Receipt recording
- Any modifications to financial records

**Using the Audit Trail:**

- Search by date range, user, or transaction type
- Filter by specific jobs or suppliers
- View the full detail of each transaction
- Export audit data for compliance and reporting
    `,
        keywords: ['audit', 'audit trail', 'history', 'transactions', 'compliance', 'log'],
        route: '/accounting',
        relatedArticles: ['accounting.overview'],
    },
    {
        id: 'accounting.variations',
        title: 'Variation Orders',
        category: 'Accounting',
        order: 8,
        summary: 'Manage contract variations (change orders) with approval workflow',
        keywords: ['variation', 'change order', 'VO', 'addition', 'omission', 'claim'],
        content: `
Variation Orders track additions and omissions to a building contract. They are managed from the **Jobs → Contract Claims** tab.

**Variation Lifecycle:**

1. **Draft** — Create and add line items
2. **Pending** — Submitted for client approval
3. **Authorised** — Approved, impacts amended contract price
4. **Rejected** — Client declined (can be deleted or revised)

**Two Modes:**

- **Simple** — Quick entry: description + debit (addition) / credit (omission) amounts
- **Detailed** — BOQ-style: cost centre, catalogue item, quantity, unit price

**Key Features:**

- Inline catalogue search in description field
- Cost centre filtering for catalogue search
- Markup % (internal, hidden from client on PDF)
- Expandable notes per line item
- PDF generation with client-facing layout and signature blocks
- OK to Claim flag for including in progress claims
- Authorisation creates a transaction (tran_type=5) for accounting
    `,
        expandableSections: [
            {
                title: 'Creating a Variation',
                content: `
1. Go to **Jobs** → open a job → **Contract Claims** tab
2. Scroll to the **Variations** section
3. Click **+ New Variation**
4. Enter a **Description** and choose **Simple** or **Detailed** mode
5. Click **Create & Edit** to open the full-screen editor
        `,
            },
            {
                title: 'Adding Line Items',
                content: `
**Simple mode:** Enter description, toggle +/- for addition or omission, enter amount, click Add Line.

**Detailed mode:** Select cost centre (searchable), enter description (or type to search catalogue — matching items appear in a dropdown), enter quantity and unit price. The amount is calculated automatically.

**From Catalogue:** Click the Catalogue button to open a search sidebar. Click items to add them as variation lines with their catalogue price.

**CC Filter toggle:** When enabled, catalogue search only shows items from the selected cost centre.
        `,
            },
            {
                title: 'Markup',
                content: `
Set the **Markup %** in the totals section. This is applied to the subtotal and included in the total shown to the client, but the breakdown is **never shown on the PDF**. The client sees only the net totals.
        `,
            },
            {
                title: 'PDF Preview',
                content: `
Click the **PDF** button in the editor header or from the table dropdown. The PDF includes:

- Company header and job details
- Client information
- Line items table (simple or detailed format)
- Additions and omissions totals
- Net variation (Inc GST)
- Signature blocks for builder and client
        `,
            },
            {
                title: 'Approval Workflow',
                content: `
1. **Submit** — moves from Draft to Pending
2. **Authorise** — enter approver name, creates a transaction for accounting
3. **Reject** — enter rejection reason
4. **OK to Claim** — flags the variation as ready to include in progress claims

Authorised variations are included in the **Amended Contract** calculation shown at the top of the Contract Claims tab.
        `,
            },
        ],
        relatedArticles: ['accounting.overview', 'jobs.overview'],
    },
];
